import type { ReactNode } from "react";
import { C } from "../tokens";
import { X } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}

const widths: Record<NonNullable<Props["size"]>, number> = {
  sm: 420, md: 640, lg: 900, xl: 1120,
};

export function Modal({ open, onClose, title, subtitle, children, footer, size = "md" }: Props) {
  if (!open) return null;
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        background: "rgba(7,13,26,0.88)",
        backdropFilter: "blur(12px)",
        animation: "cm-slide-up 0.2s ease",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: widths[size],
          maxHeight: "92vh",
          display: "flex", flexDirection: "column",
          background: C.surface,
          border: `1px solid ${C.borderBright}`,
          borderRadius: 22,
          boxShadow: `0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(45,127,249,0.1)`,
          overflow: "hidden",
        }}
      >
        {(title || subtitle) && (
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
            <div>
              {title && <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{title}</div>}
              {subtitle && <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2 }}>{subtitle}</div>}
            </div>
            <button
              onClick={onClose}
              style={{
                width: 28, height: 28, borderRadius: 8,
                background: "transparent", border: "none", color: C.textSub,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {children}
        </div>
        {footer && (
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${C.border}`, background: `${C.bg}88`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
